import { Accessibility, Bus, Car, HelpCircle, type LucideIcon } from 'lucide-react';

import { Badge } from '@/ui/base/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui/base/tooltip';

import {
  classificarTipoDeVeiculo,
  descreverFaixa,
  type TipoDeVeiculo,
  type Veiculo,
} from './contrato';

/**
 * O porte do veiculo, derivado da capacidade e da acessibilidade.
 *
 * A LISTA DE TIPOS VEM POR PROPRIEDADE, e nao de uma consulta dentro do componente. Este
 * selo aparece em TODA linha da tabela: um gancho de consulta aqui seria uma assinatura de
 * cache por linha, cinquenta por pagina, para a mesma lista parametrizada. A tela busca uma
 * vez e distribui.
 *
 * SEM CATEGORIA NAO E ERRO, E TEM DUAS CAUSAS DIFERENTES — e o tooltip separa as duas,
 * porque a providencia e diferente: capacidade nao informada e cadastro incompleto (alguem
 * precisa preencher); capacidade fora das faixas e parametrizacao incompleta (alguem precisa
 * cadastrar a faixa). Um "—" sozinho manda quem investiga para o lugar errado.
 *
 * ESTE TIPO NAO DEFINE PRECO. Quem define e o tipo de bancada, e sao coisas separadas de
 * proposito — o tooltip diz isso porque a confusao entre os dois tem efeito financeiro.
 */

interface TipoVeiculoBadgeProps {
  veiculo: Pick<Veiculo, 'capacidade_teg' | 'acessivel'> | null | undefined;
  /** A lista já carregada pela tela. Ver o comentário do topo. */
  tipos: readonly TipoDeVeiculo[] | undefined;
  tamanho?: 'sm' | 'md';
}

function iconeDoTipo(slug: string): LucideIcon {
  if (slug === 'veiculo-adaptado') {
    return Accessibility;
  }
  if (slug === 'van') {
    return Car;
  }
  // Micro-ônibus, ônibus e o que mais vier: ônibus.
  return Bus;
}

export function TipoVeiculoBadge({ veiculo, tipos, tamanho = 'sm' }: TipoVeiculoBadgeProps) {
  const tipo = classificarTipoDeVeiculo(veiculo, tipos);
  const capacidade = Number(veiculo?.capacidade_teg ?? 0);
  const escalaIcone = tamanho === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';
  const escalaTexto = tamanho === 'sm' ? 'text-xs' : 'text-sm';

  if (!tipo) {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="outline"
              className={`text-muted-foreground cursor-help gap-1 ${escalaTexto}`}
            >
              <HelpCircle className={escalaIcone} aria-hidden="true" />
              <span>—</span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[260px]">
            <p className="text-xs">
              {capacidade > 0
                ? `Capacidade TEG de ${capacidade} lugares não cabe em nenhuma faixa cadastrada.`
                : 'Capacidade TEG não informada neste veículo.'}
            </p>
            <p className="text-muted-foreground mt-0.5 text-[11px]">
              {capacidade > 0
                ? 'Falta cadastrar a faixa em Parametrização.'
                : 'Falta preencher a capacidade no cadastro do veículo.'}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const Icone = iconeDoTipo(tipo.slug);

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className={`cursor-help gap-1 ${escalaTexto}`}>
            <Icone className={escalaIcone} aria-hidden="true" />
            <span>{tipo.nome}</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px]">
          <p className="text-xs font-medium">{tipo.nome}</p>
          <p className="text-muted-foreground mt-0.5 text-[11px]">
            Faixa: {descreverFaixa(tipo)} · capacidade TEG deste veículo: {capacidade}
          </p>
          <p className="text-muted-foreground mt-1.5 border-t pt-1.5 text-[11px]">
            Categoria <strong>descritiva</strong>, tirada da capacidade e da acessibilidade. Quem
            define preço é o tipo de bancada, que é outra coisa.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
